import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';

export const OPENROUTER_MODELS_URL = process.env.REACT_APP_OPENROUTER_MODELS_URL;
export const OPENROUTER_DEFAULT_AI_MODEL = process.env.REACT_APP_OPENROUTER_DEFAULT_AI_MODEL;
export const MAX_TOP_MODELS = 100;

/**
 * Formats a token price (in USD per single token) into USD per 1M tokens.
 */
export const formatTokenPrice = (rawPrice) => {
    if (rawPrice === undefined || rawPrice === null) return '0';
    const num = Number(rawPrice);
    if (isNaN(num) || num <= 0) return '0';
    const perMillion = num * 1000000;
    return parseFloat(perMillion.toFixed(4)).toString();
};

/**
 * Generates pricing label in the format:
 * $1/$5 per 1M tokens
 */
export const getPricingLabel = (pricing) => {
    if (!pricing) return '$0/$0 per 1M tokens';
    const input = formatTokenPrice(pricing.prompt);
    const output = formatTokenPrice(pricing.completion);
    return `$${input}/$${output} per 1M tokens`;
};

/**
 * Dynamic AI model selector.
 * Fetches available models from OpenRouter, limits to top 100,
 * and renders model names with their pricing displayed below.
 */
const ModelPicker = ({
    selectedModel,
    onModelChange,
    disabled = false,
    className = '',
    style,
}) => {
    const [models, setModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    const {
        ref,
        isComponentVisible,
        setIsComponentVisible,
    } = useComponentVisible(false);

    useEffect(() => {
        if (isComponentVisible) {
            requestAnimationFrame(() => {
                searchInputRef.current?.focus({ preventScroll: true });
            });
        } else {
            setSearchQuery('');
        }
    }, [isComponentVisible]);

    const selectedModelRef = useRef(selectedModel);
    useEffect(() => {
        selectedModelRef.current = selectedModel;
    }, [selectedModel]);

    const onModelChangeRef = useRef(onModelChange);
    useEffect(() => {
        onModelChangeRef.current = onModelChange;
    }, [onModelChange]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchModels = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(OPENROUTER_MODELS_URL, {
                    signal: controller.signal,
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch models: ${response.status}`);
                }
                const result = await response.json();
                if (Array.isArray(result?.data)) {
                    const dynamicModels = result.data
                        .filter((item) => item?.id)
                        .map((item) => ({
                            id: item.id,
                            label: item.name || item.id,
                            pricingText: getPricingLabel(item.pricing),
                        }));

                    setModels(dynamicModels);

                    if (dynamicModels.length > 0) {
                        const current = selectedModelRef.current;
                        if (!current) {
                            const defaultModel = dynamicModels.find(m => m.id === OPENROUTER_DEFAULT_AI_MODEL);
                            if (OPENROUTER_DEFAULT_AI_MODEL && defaultModel) {
                                onModelChangeRef.current?.(defaultModel.id);
                            } else {
                                onModelChangeRef.current?.(dynamicModels[0].id);
                            }
                        }
                    }
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error('Error fetching OpenRouter models:', error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        fetchModels();

        return () => {
            controller.abort();
        };
    }, []);

    const filteredModels = useMemo(() => {
        if (!searchQuery.trim()) return models;
        const query = searchQuery.toLowerCase();
        return models.filter(
            (m) =>
                m.label.toLowerCase().includes(query) ||
                m.id.toLowerCase().includes(query)
        );
    }, [models, searchQuery]);

    const currentModel =
        models.find((m) => m.id === selectedModel) ||
        (selectedModel
            ? { id: selectedModel, label: selectedModel, pricingText: '' }
            : models[0] || null);

    return (
        <div
            ref={ref}
            className={`sai-model-picker relative ${className}`.trim()}
            style={{ margin: 0, marginRight: '15px', ...style }}
        >
            <span className="shrink-0">AI model</span>

            {/* Custom dropdown trigger */}
            <button
                type="button"
                className="sai-model-picker__trigger flex items-center justify-between gap-2 px-3 py-1.5 border border-[#d6d6d6] rounded-lg bg-[var(--sai-surface,#fff)] hover:border-gray-400 text-left min-w-[200px] max-w-[320px] transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                onClick={() => !disabled && !isLoading && setIsComponentVisible((prev) => !prev)}
                disabled={disabled || isLoading}
                aria-haspopup="listbox"
                aria-expanded={isComponentVisible}
                aria-label="AI model selector"
            >
                <div className="flex flex-col min-w-0 pr-1 overflow-hidden">
                    <span className="text-xs font-semibold text-gray-900 truncate">
                        {isLoading && models.length === 0
                            ? 'Loading models…'
                            : currentModel?.label || selectedModel || 'Select model'}
                    </span>
                </div>
                <ChevronDown
                    size={14}
                    className={`shrink-0 text-gray-500 transition-transform duration-200 ${
                        isComponentVisible ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Custom dropdown menu (opens upward above footer) */}
            {isComponentVisible && (
                <div
                    className="absolute bottom-full mb-2 right-0 w-80 max-w-[calc(100vw-32px)] bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden flex flex-col"
                    style={{ maxHeight: '280px' }}
                >
                    <div className="p-2 border-b border-gray-100 bg-gray-50 sticky top-0 z-10 flex items-center gap-2">
                        <Search size={14} className="text-gray-400 shrink-0 ml-1" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search models…"
                            className="w-full text-xs bg-transparent border-none focus:outline-none placeholder-gray-400 text-gray-700"
                        />
                    </div>
                    <ul
                        className="overflow-y-auto divide-y divide-gray-50 py-1"
                        role="listbox"
                        style={{ maxHeight: '250px' }}
                    >
                        {filteredModels.length > 0 ? (
                            filteredModels.slice(0, MAX_TOP_MODELS).map((model) => {
                                const isSelected = model.id === selectedModel;
                                return (
                                    <li
                                        key={model.id}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`px-3 py-2 cursor-pointer flex flex-col text-left transition-colors ${
                                            isSelected
                                                ? 'bg-blue-50 text-blue-900 font-medium'
                                                : 'hover:bg-gray-50 text-gray-800'
                                        }`}
                                        onClick={() => {
                                            onModelChange?.(model.id);
                                            setIsComponentVisible(false);
                                        }}
                                    >
                                        <span className="text-xs font-medium text-gray-900 truncate">
                                            {model.label}
                                        </span>
                                        <span className="text-[11px] text-gray-500 font-normal mt-0.5">
                                            {model.pricingText}
                                        </span>
                                    </li>
                                );
                            })
                        ) : (
                            <li className="px-3 py-4 text-xs text-center text-gray-500">
                                No models found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ModelPicker;
